import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Avatar,
  IconButton,
  Divider,
  Autocomplete,
  Chip,
  Alert,
  Switch,
  Paper,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import TagIcon from "@mui/icons-material/Tag";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { useTranslation } from "react-i18next";
import { useUsers } from "../../../hooks/useUsers";
import { useAgents } from "../../../hooks/useAgents";
import { getInitials } from "../../../utils/utils";
import TeamPresenceAvatar from "../../../components/TeamPresenceAvatar";
import { useTeamPresenceOptional } from "../../../context/TeamPresenceContext";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import { TEAM_MOBILE_MEDIA_QUERY } from "../utils/teamUiUtils";

const sectionLabelSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: 1.25,
};

function SettingsSection({ title, children, sx }) {
  return (
    <Box sx={sx}>
      {title ? (
        <Typography component="p" sx={sectionLabelSx}>
          {title}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

const TeamSpaceSettingsDialog = ({
  open,
  onClose,
  space,
  members,
  currentUserId,
  canManageChannel,
  onUpdateSpace,
  onAddMember,
  onRemoveMember,
  onDeleteSpace,
  isBusy,
}) => {
  const { t } = useTranslation("team");
  const { t: tLive } = useTranslation("settings");
  const theme = useTheme();
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const isDm = space?.type === "dm";
  const isAgentDm = isDm && space?.dmPeerType === "agent";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: usersData } = useUsers(1, 200, "", "humanos");
  const { data: agentsData } = useAgents(1, 100, "");
  const presenceCtx = useTeamPresenceOptional();
  const users = usersData?.users || [];
  const agents = agentsData?.agents || [];

  const memberUserIds = useMemo(
    () =>
      new Set(
        (members || [])
          .filter((m) => m.participantType === "user")
          .map((m) => String(m.participantId))
      ),
    [members]
  );

  const memberAgentIds = useMemo(
    () =>
      new Set(
        (members || [])
          .filter((m) => m.participantType === "agent")
          .map((m) => String(m.participantId))
      ),
    [members]
  );

  const addableTargets = useMemo(() => {
    const people = users
      .filter((u) => u?.id && !memberUserIds.has(String(u.id)))
      .map((u) => ({
        kind: "user",
        id: u.id,
        label: u.nome || u.name || u.email || "",
      }));
    const aiAgents = agents
      .filter((a) => a?.id && !memberAgentIds.has(String(a.id)))
      .map((a) => ({
        kind: "agent",
        id: a.id,
        label: a.name || a.id || "",
      }));
    return [...people, ...aiAgents];
  }, [users, agents, memberUserIds, memberAgentIds]);

  const currentUserMember = useMemo(
    () =>
      (members || []).find(
        (m) =>
          m.participantType === "user" &&
          String(m.participantId) === String(currentUserId)
      ),
    [members, currentUserId]
  );

  const humanCount = (members || []).filter((m) => m.participantType === "user").length;
  const agentCount = (members || []).filter((m) => m.participantType === "agent").length;

  useEffect(() => {
    if (!open) {
      setConfirmDelete(false);
      setErrorMsg("");
      return;
    }
    setName(space?.name || "");
    setDescription(space?.description || "");
    setIsPrivate(Boolean(space?.isPrivate));
  }, [open, space]);

  const handleSaveChannel = async () => {
    if (!space?.id || !canManageChannel) return;
    setErrorMsg("");
    try {
      await onUpdateSpace({
        name: name.trim(),
        description: description.trim() || null,
        isPrivate,
      });
      onClose();
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err, t("channelUpdateError")));
    }
  };

  const handleAddTarget = async (_, option) => {
    if (!option?.id || !space?.id) return;
    setErrorMsg("");
    try {
      await onAddMember({
        participantType: option.kind === "agent" ? "agent" : "user",
        participantId: String(option.id),
      });
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err, t("memberAddError")));
    }
  };

  const handleRemoveMember = async (member) => {
    if (!member?.id) return;
    setErrorMsg("");
    try {
      await onRemoveMember(member.id);
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err, t("memberRemoveError")));
    }
  };

  const handleDelete = async () => {
    if (!space?.id) return;
    setErrorMsg("");
    try {
      await onDeleteSpace();
      onClose();
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err, isDm ? t("dmDeleteError") : t("channelDeleteError")));
    }
  };

  const roleChip = (member) => {
    if (member.participantType === "agent") {
      return <Chip size="small" label={t("agentMessage")} color="secondary" variant="outlined" sx={{ height: 22 }} />;
    }
    if (member.participantType === "chatbot") {
      return <Chip size="small" label={t("chatbotMessage")} variant="outlined" sx={{ height: 22 }} />;
    }
    if (member.role === "owner") {
      return <Chip size="small" label={t("roleOwner")} color="primary" sx={{ height: 22 }} />;
    }
    if (member.role === "admin") {
      return <Chip size="small" label={t("roleAdmin")} variant="outlined" sx={{ height: 22 }} />;
    }
    return null;
  };

  const canRemoveMember = (member) => {
    if (!canManageChannel && member.participantType === "user") {
      return (
        String(member.participantId) === String(currentUserId) &&
        member.role !== "owner"
      );
    }
    if (member.role === "owner") return false;
    return canManageChannel;
  };

  const displayTitle = isDm
    ? space?.dmPeerName || t("directMessages")
    : space?.name || (space?.slug ? `#${space.slug}` : "");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2.5, overflow: "hidden" } }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          {isDm ? (
            isAgentDm ? (
              <Avatar sx={{ width: 48, height: 48, bgcolor: "secondary.main" }}>
                <SmartToyOutlinedIcon />
              </Avatar>
            ) : (
              <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                {getInitials(displayTitle)}
              </Avatar>
            )
          ) : (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              {space?.isPrivate ? <LockOutlinedIcon /> : <TagIcon />}
            </Box>
          )}

          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.3} noWrap>
              {isDm ? t("dmSettingsTitle") : t("channelSettingsTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {isDm
                ? displayTitle
                : displayTitle.startsWith("#")
                  ? displayTitle
                  : `#${displayTitle}`}
            </Typography>
            {!isDm ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                <Chip
                  size="small"
                  icon={space?.isPrivate ? <LockOutlinedIcon /> : <TagIcon />}
                  label={space?.isPrivate ? t("privateChannelShort") : t("publicChannelShort")}
                  sx={{ height: 24 }}
                />
                <Chip
                  size="small"
                  icon={<GroupOutlinedIcon />}
                  label={t("membersCountShort", { count: humanCount + agentCount })}
                  sx={{ height: 24 }}
                />
              </Box>
            ) : null}
          </Box>

          <IconButton size="small" onClick={onClose} aria-label={t("close")} sx={{ mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {errorMsg ? (
          <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ borderRadius: 2 }}>
            {errorMsg}
          </Alert>
        ) : null}

        {isDm ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}>
            <Box sx={{ display: "flex", gap: 1.25 }}>
              <InfoOutlinedIcon color="action" sx={{ fontSize: 20, mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                {t("dmSettingsHint")}
              </Typography>
            </Box>
          </Paper>
        ) : canManageChannel ? (
          <SettingsSection title={t("generalSection")}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label={t("channelName")}
                placeholder={t("channelNameHint")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label={t("channelDescription")}
                placeholder={t("channelDescriptionHint")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {t("privateChannel")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("privateChannelHint")}
                  </Typography>
                </Box>
                <Switch
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  color="primary"
                />
              </Box>
            </Paper>
          </SettingsSection>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("channelSettingsReadOnly")}
            </Typography>
            {space?.description ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {space.description}
              </Typography>
            ) : null}
          </Paper>
        )}

        {!isDm ? (
          <SettingsSection title={t("membersSection")}>
            {canManageChannel ? (
              <Autocomplete
                options={addableTargets}
                groupBy={(o) => (o.kind === "agent" ? t("agentsGroup") : t("peopleGroup"))}
                getOptionLabel={(o) => o.label || ""}
                isOptionEqualToValue={(a, b) => a.kind === b.kind && String(a.id) === String(b.id)}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={`${option.kind}-${option.id}`}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      {option.kind === "agent" ? (
                        <Avatar sx={{ width: 28, height: 28, bgcolor: "secondary.main" }}>
                          <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                      ) : (
                        <TeamPresenceAvatar
                          userId={option.id}
                          name={option.label}
                          size={28}
                          dotSize={8}
                        />
                      )}
                      <Typography variant="body2">{option.label}</Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={t("addParticipantPlaceholder")}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <PersonAddOutlinedIcon color="action" sx={{ ml: 0.5, mr: 0.25, fontSize: 20 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                )}
                onChange={handleAddTarget}
                value={null}
                blurOnSelect
                clearOnBlur
                disabled={isBusy || addableTargets.length === 0}
              />
            ) : null}

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                maxHeight: 260,
                overflowY: "auto",
              }}
              className="sidebar-scroll"
            >
              {(members || []).length ? (
                (members || []).map((member, index) => {
                  const isAgent =
                    member.participantType === "agent" || member.participantType === "chatbot";
                  const removable = canRemoveMember(member);
                  const isSelf =
                    member.participantType === "user" &&
                    String(member.participantId) === String(currentUserId);

                  return (
                    <Box key={member.id}>
                      {index > 0 ? <Divider /> : null}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.25,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        {isAgent ? (
                          <Avatar sx={{ width: 40, height: 40, bgcolor: "secondary.main" }}>
                            <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />
                          </Avatar>
                        ) : (
                          <TeamPresenceAvatar
                            userId={member.participantId}
                            src={member.avatarUrl}
                            name={member.displayName}
                            size={40}
                          />
                        )}

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {member.displayName}
                              {isSelf ? ` (${t("you")})` : ""}
                            </Typography>
                            {roleChip(member)}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {member.participantType === "user"
                              ? (() => {
                                  const pk = presenceCtx?.getPresence(member.participantId)?.kind;
                                  if (pk === "online") return tLive("dashboardPage.live.onlineNow");
                                  if (pk === "away") return tLive("dashboardPage.live.away");
                                  if (pk === "offline") return tLive("dashboardPage.live.presenceOffline");
                                  return member.role === "member" ? t("roleMember") : null;
                                })()
                              : null}
                          </Typography>
                        </Box>

                        {removable ? (
                          <Tooltip title={isSelf ? t("leaveChannel") : t("removeMember")}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveMember(member)}
                                disabled={isBusy}
                                sx={{ color: "text.secondary" }}
                              >
                                <PersonRemoveOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("noMembersYet")}
                  </Typography>
                </Box>
              )}
            </Paper>

            {!canManageChannel && currentUserMember && currentUserMember.role !== "owner" ? (
              <Button
                color="warning"
                variant="outlined"
                startIcon={<PersonRemoveOutlinedIcon />}
                onClick={() => handleRemoveMember(currentUserMember)}
                disabled={isBusy}
                sx={{ mt: 0.5, alignSelf: "flex-start", textTransform: "none", borderRadius: 2 }}
              >
                {t("leaveChannel")}
              </Button>
            ) : null}
          </SettingsSection>
        ) : null}

        <SettingsSection title={t("dangerZone")}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: alpha(theme.palette.error.main, 0.35),
              bgcolor: alpha(theme.palette.error.main, 0.04),
            }}
          >
            {!confirmDelete ? (
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="error.main" gutterBottom>
                    {isDm ? t("hideDm") : t("deleteChannel")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isDm ? t("hideDmConfirmShort") : t("deleteChannelConfirmShort")}
                  </Typography>
                </Box>
                <Button
                  color="error"
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setConfirmDelete(true)}
                  sx={{ textTransform: "none", flexShrink: 0, borderRadius: 2 }}
                >
                  {isDm ? t("hideDm") : t("deleteChannel")}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {isDm ? t("hideDmConfirm") : t("deleteChannelConfirm")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                  <Button
                    onClick={() => setConfirmDelete(false)}
                    sx={{ textTransform: "none" }}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    color="error"
                    variant="contained"
                    onClick={handleDelete}
                    disabled={isBusy}
                    sx={{ textTransform: "none", borderRadius: 2 }}
                  >
                    {isDm ? t("hideDm") : t("deleteChannel")}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </SettingsSection>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "text.secondary" }}>
          {t("close")}
        </Button>
        {!isDm && canManageChannel ? (
          <Button
            variant="contained"
            onClick={handleSaveChannel}
            disabled={!name.trim() || isBusy}
            sx={{ textTransform: "none", borderRadius: 2, px: 2.5 }}
          >
            {t("saveChanges")}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default TeamSpaceSettingsDialog;
